import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import {
  Badge,
  Button,
  Card,
  Input,
  MagnusOperaIcon,
  ThemeToggle,
} from "@insights/ui";
import { authClient } from "../../../api";
import { newPasswordError } from "../helpers/password-reset";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [complete, setComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");

    const validationError = newPasswordError(password, confirmation);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!token) {
      setError("This password-reset link is invalid or has expired");
      return;
    }

    setSubmitting(true);
    const result = await authClient.resetPassword({
      newPassword: password,
      token,
    });
    setSubmitting(false);

    if (result.error) {
      setError("This password-reset link is invalid or has expired");
      return;
    }
    setComplete(true);
  }

  return (
    <main className="auth-shell">
      <ThemeToggle className="auth-theme" />
      <section className="auth-copy">
        <Badge>Account recovery</Badge>
        <h1>
          Start again,
          <br />
          <em>securely.</em>
        </h1>
        <p>Choose a new password for your Insights account.</p>
      </section>
      <Card className="auth-card">
        <div className="mark">
          <MagnusOperaIcon /> Insights
        </div>
        <h2>{complete ? "Password updated" : "Choose a new password"}</h2>
        {complete ? (
          <>
            <p>Your password has been updated. You can now sign in.</p>
            <Link className="auth-text-link" to="/">
              Continue to sign in
            </Link>
          </>
        ) : !token ? (
          <>
            <p>This password-reset link is invalid or has expired.</p>
            <Link className="auth-text-link" to="/forgot-password">
              Request another link
            </Link>
          </>
        ) : (
          <>
            <p>Use at least eight characters for your new password.</p>
            <form onSubmit={submit}>
              <label>
                New password
                <Input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={8}
                  autoComplete="new-password"
                  required
                />
              </label>
              <label>
                Confirm new password
                <Input
                  type="password"
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  minLength={8}
                  autoComplete="new-password"
                  required
                />
              </label>
              {error && <div className="error">{error}</div>}
              <Button type="submit" disabled={submitting}>
                {submitting ? "Updating…" : "Update password"}
              </Button>
            </form>
          </>
        )}
      </Card>
    </main>
  );
}
